import { useState, useCallback, useId, Children, cloneElement } from 'react';
import type { ReactElement, KeyboardEvent, DragEvent } from 'react';

import defaultTranslations, { SortableListDefaultTranslations } from './sortableListTranslations';

import { styled } from '../styles';

import BaseOl from '../BaseOl';
import SortableListItem, { Props as ListItemProps } from '../SortableListItem';

type SortableListItemElement = ReactElement<ListItemProps, typeof SortableListItem>;
type ItemData = ListItemProps['itemData'];
type OrderedItems = Array<ItemData>;
type ItemInfo = {
  item: ItemData | null;
  currentPosition: number;
  itemCount: number;
};

/* istanbul ignore next */
const noop = () => {};

export interface Props {
  children: SortableListItemElement[];
  ListComponent?: typeof BaseOl;
  onOrderChange?: (items: OrderedItems) => void;
  onSelectChange?: (itemData: ItemData | null) => void;
  onSortEnd?: (data: {
    orderedItems: OrderedItems;
    lastSortedItem: ItemData;
    lastSortedItemIndex: number;
  }) => void;
  translations?: SortableListDefaultTranslations;
}

const AssistiveText = styled('span')({
  position: 'absolute',
  margin: -1,
  border: 0,
  padding: 0,
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
});

const SortableList = ({
  children,
  ListComponent = BaseOl,
  onOrderChange = noop,
  onSelectChange = noop,
  onSortEnd = noop,
  translations = defaultTranslations,
}: Props) => {
  const [currentFocusedItem, setCurrentFocusedItem] = useState<ItemData | null>(null);
  const [currentSelectedItem, setCurrentSelectedItem] = useState<ItemData | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [liveText, setLiveText] = useState('');

  const instructionId = useId();

  const itemDataFromChildren: OrderedItems = Children.map(
    children,
    (child) => child.props.itemData
  );

  const itemCount = itemDataFromChildren.length;

  const [orderedItems, setOrderedItems] = useState<OrderedItems>(itemDataFromChildren);

  const orderedItemCount = orderedItems.length;

  const indexOfItem = useCallback(
    (itemToFind: ItemData | null): number =>
      orderedItems.findIndex((item) => item.name === itemToFind?.name),
    [orderedItems]
  );

  const currentSelectedItemIndex = indexOfItem(currentSelectedItem);

  const childrenAsArray = Children.toArray(children).sort(
    (a: SortableListItemElement, b: SortableListItemElement) =>
      indexOfItem(a.props.itemData) - indexOfItem(b.props.itemData)
  );

  const changeSelectedItem = useCallback(
    (newSelectedItem: ItemData | null) => {
      onSelectChange(newSelectedItem);
      if (currentSelectedItem !== null && newSelectedItem === null) {
        onSortEnd({
          orderedItems,
          lastSortedItem: currentSelectedItem,
          lastSortedItemIndex: indexOfItem(currentSelectedItem),
        });
      }

      setCurrentSelectedItem(newSelectedItem);
    },
    [currentSelectedItem, onSelectChange, orderedItems, onSortEnd, indexOfItem]
  );

  const swapItems = useCallback(
    (targetItemIndex: number): number => {
      const currentItem = orderedItems[currentSelectedItemIndex];

      let nextItemIndex = targetItemIndex;

      if (nextItemIndex < 0) {
        nextItemIndex = itemCount - 1;
      } else if (nextItemIndex >= itemCount) {
        nextItemIndex = 0;
      }

      const targetItem = orderedItems[nextItemIndex];

      const newItems = [...orderedItems];

      newItems[currentSelectedItemIndex] = targetItem;
      newItems[nextItemIndex] = currentItem;

      setOrderedItems(newItems);
      onOrderChange(newItems);

      return nextItemIndex;
    },
    [orderedItems, currentSelectedItemIndex, itemCount, onOrderChange]
  );

  if (itemCount !== orderedItemCount) {
    setOrderedItems(itemDataFromChildren);
    return null;
  }

  const unselect = () => changeSelectedItem(null);

  const onKeyDownHandler = (e: KeyboardEvent) => {
    const getItemInfo = (itemIndex: number): ItemInfo => ({
      item: currentFocusedItem,
      currentPosition: itemIndex + 1,
      itemCount,
    });

    const handleArrows = (direction: 1 | -1) => {
      if (currentSelectedItem === null) {
        return;
      }
      const newIndex = swapItems(currentSelectedItemIndex + direction);

      e.stopPropagation();
      e.preventDefault();

      const movedItemInfo = {
        ...getItemInfo(currentSelectedItemIndex),
        currentPosition: newIndex + 1,
      };

      setLiveText(translations.itemMoved(movedItemInfo));
    };

    switch (e.key) {
      case 'Tab':
        unselect();
        break;

      case 'ArrowUp':
      case 'ArrowLeft':
        handleArrows(-1);
        break;

      case 'ArrowDown':
      case 'ArrowRight':
        handleArrows(1);
        break;

      case ' ': {
        e.stopPropagation();
        e.preventDefault();

        if (currentSelectedItem === null) {
          setLiveText(translations.itemSelected(getItemInfo(indexOfItem(currentFocusedItem))));
          changeSelectedItem(currentFocusedItem);
        } else {
          setLiveText(translations.itemDropped(getItemInfo(currentSelectedItemIndex)));
          changeSelectedItem(null);
        }

        break;
      }

      /* istanbul ignore next */
      default:
        break;
    }
  };

  const onFocusHandler = (item: ItemData) => {
    setCurrentFocusedItem(item);
  };

  /* istanbul ignore next (don't know how to test drag with jsdom :( )) */
  const getDragStartHandler = (item: ItemData) => (e: DragEvent<HTMLLIElement>) => {
    changeSelectedItem(item);
    setIsDragging(true);

    e.dataTransfer.setData('text/plain', item.name); // Require for mobile to work
    e.dataTransfer.effectAllowed = 'move';
  };

  /* istanbul ignore next (don't know how to test drag with jsdom :( )) */
  const getDragOverHandler = (overedItemIndex: number) => (e: DragEvent<HTMLLIElement>) => {
    e.preventDefault();
    if (currentSelectedItemIndex === overedItemIndex) {
      return;
    }

    e.dataTransfer.dropEffect = 'move';

    swapItems(overedItemIndex);
  };

  /* istanbul ignore next (don't know how to test drag with jsdom :( )) */
  const dragEndHandler = () => {
    unselect();
    setIsDragging(false);
  };

  /* istanbul ignore next (don't know how to test drag with jsdom :( )) */
  const cursor = isDragging ? 'grabbing' : 'grab';

  return (
    <>
      <AssistiveText aria-live="assertive">{liveText}</AssistiveText>
      <AssistiveText id={instructionId}>{translations.instructions}</AssistiveText>
      <ListComponent
        aria-label={translations.ariaLabel}
        role="listbox"
        onKeyDown={(e) => onKeyDownHandler(e)}
      >
        {childrenAsArray.map((child: SortableListItemElement, index) => {
          const { itemData, style } = child.props;

          return cloneElement(child, {
            key: itemData.name,
            role: 'option',
            draggable: true,
            onDragStart: getDragStartHandler(itemData),
            onDragOver: getDragOverHandler(index),
            onDragEnd: dragEndHandler,
            'aria-label': itemData.name,
            'aria-describedby': instructionId,
            tabIndex: 0,
            onFocus: () => onFocusHandler(itemData),
            style: {
              opacity: currentSelectedItem?.name === itemData.name ? 0.5 : 1,
              cursor,
              ...style,
            },
          });
        })}
      </ListComponent>
    </>
  );
};

export default SortableList;
